import type { Tab } from './index';
import { fetchSheet, getWorkerBase } from '../worker';

interface PricePoint { date: Date; price: number; }
interface ChartPoint { date: Date; value: number; }
interface Portfolio  { label: string; color: string; tickers: string[]; entryPrices: Record<string, number>; }

const TF_MAP: Record<string, { range: string; interval: string }> = {
  YTD: { range:'ytd', interval:'1d'  },
  '3M':{ range:'3mo', interval:'1d'  },
  '6M':{ range:'6mo', interval:'1wk' },
  '1Y':{ range:'1y',  interval:'1wk' },
};

const TICKER_COLORS = ['#00ff9d','#f472b6','#fbbf24','#a78bfa','#fb923c','#34d399','#60a5fa','#f87171','#c084fc','#4ade80'];

export class PerformanceTab implements Tab {
  readonly id = 'performance';

  private portfolios: Record<string, Portfolio> = {};
  private currentView  = '';
  private currentTF    = '3M';
  private currentWeight: 'equal' | 'price' = 'equal';
  private chartData: Record<string, ChartPoint[]> = {};

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('perf-refresh-btn').addEventListener('click', () => this.load());
    document.querySelectorAll<HTMLElement>('[data-perf-tf]').forEach(btn =>
      btn.addEventListener('click', () => this.setTF(btn.dataset.perfTf ?? '3M')),
    );
    document.querySelectorAll<HTMLElement>('[data-perf-weight]').forEach(btn =>
      btn.addEventListener('click', () => this.setWeight(btn.dataset.perfWeight as 'equal' | 'price')),
    );
  }

  render(): void {
    if (!Object.keys(this.portfolios).length) {
      this.loadPortfolios().then(() => this.load());
    } else {
      this.load();
    }
  }

  private setTF(tf: string): void {
    this.currentTF = tf;
    ['YTD','3M','6M','1Y'].forEach(t => {
      const btn = document.getElementById(`ptf-${t}`);
      if (!btn) return;
      const active = t === tf;
      btn.style.background  = active ? 'rgba(0,212,255,0.15)' : 'transparent';
      btn.style.borderColor = active ? 'rgba(0,212,255,0.4)'  : 'var(--border)';
      btn.style.color       = active ? 'var(--accent)'        : 'var(--text3)';
    });
    this.load();
  }

  private setWeight(weight: 'equal' | 'price'): void {
    this.currentWeight = weight;
    ['equal','price'].forEach(w => {
      const btn = document.getElementById(`pw-${w}`);
      if (!btn) return;
      const active = w === weight;
      btn.style.background  = active ? 'rgba(0,255,157,0.1)' : 'transparent';
      btn.style.borderColor = active ? 'rgba(0,255,157,0.3)' : 'var(--border)';
      btn.style.color       = active ? 'var(--accent2)'      : 'var(--text3)';
    });
    if (Object.keys(this.chartData).length) this.renderChart();
  }

  private setView(key: string): void {
    this.currentView = key;
    Object.keys(this.portfolios).forEach(k => {
      const btn = document.getElementById(`pv-${k}`);
      if (!btn) return;
      const active = k === key;
      btn.style.background  = active ? 'rgba(0,212,255,0.15)' : 'transparent';
      btn.style.borderColor = active ? 'rgba(0,212,255,0.4)'  : 'var(--border)';
      btn.style.color       = active ? 'var(--accent)'        : 'var(--text3)';
    });
    this.load();
  }

  private async loadPortfolios(): Promise<void> {
    const container = this.el('perf-portfolio-buttons');
    if (!getWorkerBase()) {
      container.innerHTML = '<span style="color:var(--warn);font-size:11px;">Worker URL not configured</span>';
      return;
    }
    container.innerHTML = '<span style="color:var(--text3);font-size:11px;letter-spacing:0.08em;">LOADING…</span>';
    try {
      const data = await fetchSheet('PerfPortfolios');
      if (data.status !== 'ok') throw new Error(data.message ?? 'Failed to load portfolios');
      const rows = data.rows ?? [];
      if (!rows.length) throw new Error('No rows in PerfPortfolios sheet');

      this.portfolios = {};
      for (const r of rows) {
        const key = ((r as any).Name as string || '').toLowerCase().replace(/[^a-z0-9]/g,'') || String(Object.keys(this.portfolios).length);
        const tickers = ((r as any).Tickers as string || '').split(',').map((t: string) => t.trim()).filter(Boolean);
        const entryPrices: Record<string, number> = {};
        ((r as any).EntryPrices as string || '').split(',').forEach((pair: string) => {
          const [k, v] = pair.trim().split(':');
          const n = Number(v);
          if (k && !isNaN(n)) entryPrices[k.trim()] = n;
        });
        this.portfolios[key] = { label:(r as any).Name || key, color:(r as any).Color || '#00d4ff', tickers, entryPrices };
      }

      this.currentView = Object.keys(this.portfolios)[0] ?? '';
      container.innerHTML = Object.keys(this.portfolios).map((key, i) => {
        const p = this.portfolios[key];
        const active = i === 0;
        return `<button data-pv="${key}" style="background:${active?'rgba(0,212,255,0.15)':'transparent'};border:1px solid ${active?'rgba(0,212,255,0.4)':'var(--border)'};color:${active?'var(--accent)':'var(--text3)'};font-family:var(--mono);font-size:11px;padding:6px 14px;cursor:pointer;border-radius:3px;transition:all 0.2s;">${p.label}</button>`;
      }).join('');
      container.querySelectorAll<HTMLElement>('[data-pv]').forEach(btn =>
        btn.addEventListener('click', () => this.setView(btn.dataset.pv ?? '')),
      );
    } catch (err: unknown) {
      container.innerHTML = `<span style="color:var(--warn);font-size:11px;">Load failed: ${esc(String(err))}</span>`;
    }
  }

  private async fetchHistorical(ticker: string, range: string, interval: string): Promise<PricePoint[] | null> {
    const workerBase = getWorkerBase();
    if (!workerBase) return null;
    try {
      const url   = `https://query1.finance.yahoo.com/v8/finance/chart/${ticker}?range=${range}&interval=${interval}&includePrePost=false`;
      const proxy = `${workerBase}/api/proxy?url=${encodeURIComponent(url)}`;
      const res   = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
      const raw   = await res.json() as { contents: string };
      const parsed= JSON.parse(raw.contents);
      const result= parsed?.chart?.result?.[0];
      if (!result) return null;
      const timestamps: number[] = result.timestamp ?? [];
      const closes: (number | null)[] = result.indicators?.quote?.[0]?.close ?? [];
      return timestamps.map((ts, i) => ({ date: new Date(ts*1000), price: closes[i] as number })).filter(p => p.price != null);
    } catch { return null; }
  }

  private normalize(points: PricePoint[]): ChartPoint[] {
    if (!points.length) return [];
    const base = points[0].price;
    return points.map(p => ({ date: p.date, value: (p.price / base) * 100 }));
  }

  private async load(): Promise<void> {
    const portfolio = this.portfolios[this.currentView];
    if (!portfolio) return;
    const { range, interval } = TF_MAP[this.currentTF] ?? TF_MAP['3M'];

    this.el('perf-loading').style.display = 'block';
    this.el('perf-error').style.display   = 'none';
    const canvas = this.el('perf-canvas') as HTMLCanvasElement;
    canvas.style.display = 'none';

    const allTickers = ['^GSPC', ...portfolio.tickers];
    const results = await Promise.all(
      allTickers.map(t => this.fetchHistorical(t, range, interval).then(d => ({ ticker: t, data: d }))),
    );

    this.el('perf-loading').style.display = 'none';

    const dataMap: Record<string, ChartPoint[]> = {};
    for (const r of results) {
      if (r.data?.length) dataMap[r.ticker] = this.normalize(r.data);
    }

    if (!dataMap['^GSPC']) {
      this.el('perf-error').style.display = 'block';
      this.el('perf-error').textContent = 'Could not fetch S&P 500 data. The proxy may be rate-limited — try again in a moment.';
      return;
    }

    this.chartData = dataMap;
    canvas.style.display = 'block';
    this.el('perf-last-updated').textContent = `Updated ${new Date().toLocaleTimeString()}`;
    this.renderChart();
    this.buildTickerCards(portfolio, dataMap);
  }

  private buildPortfolioLine(portfolio: Portfolio, dataMap: Record<string, ChartPoint[]>): ChartPoint[] {
    const sp500Dates   = dataMap['^GSPC'].map(p => p.date.getTime());
    const validTickers = portfolio.tickers.filter(t => dataMap[t]);
    if (!validTickers.length) return [];
    return sp500Dates.map((ts, _i) => {
      let totalW = 0, weightedSum = 0;
      for (const ticker of validTickers) {
        const series  = dataMap[ticker];
        const closest = series.reduce((a, b) => Math.abs(b.date.getTime()-ts) < Math.abs(a.date.getTime()-ts) ? b : a);
        const w = this.currentWeight === 'price' ? (portfolio.entryPrices[ticker] ?? 1) : 1;
        weightedSum += closest.value * w; totalW += w;
      }
      return { date: new Date(ts), value: totalW > 0 ? weightedSum / totalW : 100 };
    });
  }

  private renderChart(): void {
    const portfolio = this.portfolios[this.currentView];
    const canvas    = this.el('perf-canvas') as HTMLCanvasElement;
    if (!canvas || !portfolio || !Object.keys(this.chartData).length) return;

    const sp500         = this.chartData['^GSPC'];
    const portfolioLine = this.buildPortfolioLine(portfolio, this.chartData);
    const dpr    = window.devicePixelRatio || 1;
    const rect   = canvas.parentElement!.getBoundingClientRect();
    const W = rect.width; const H = 420;
    canvas.width  = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';

    const ctx    = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    const margin = { top:20, right:20, bottom:40, left:55 };
    const iW = W - margin.left - margin.right;
    const iH = H - margin.top  - margin.bottom;

    ctx.clearRect(0, 0, W, H);

    const allVals = [...sp500.map(p=>p.value), ...portfolioLine.map(p=>p.value)];
    const minV = Math.min(...allVals) * 0.98;
    const maxV = Math.max(...allVals) * 1.02;
    const minD = sp500[0].date.getTime();
    const maxD = sp500[sp500.length-1].date.getTime();
    const xScale = (d: number) => margin.left + (d-minD) / (maxD-minD) * iW;
    const yScale = (v: number) => margin.top  + (1 - (v-minV) / (maxV-minV)) * iH;

    const dateStep = Math.max(1, Math.floor(sp500.length / 6));

    const drawStatic = (c: CanvasRenderingContext2D) => {
      c.clearRect(0, 0, W, H);
      c.strokeStyle = 'rgba(30,52,72,0.5)'; c.setLineDash([3,3]);
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + iH * i / 5;
        c.beginPath(); c.moveTo(margin.left, y); c.lineTo(margin.left+iW, y); c.stroke();
        const val = maxV - (maxV-minV)*i/5;
        c.fillStyle='#3d6478'; c.font='10px Space Mono,monospace'; c.textAlign='right';
        c.fillText(val.toFixed(1), margin.left-6, y+4);
      }
      c.setLineDash([]);
      const y100 = yScale(100);
      c.strokeStyle='rgba(122,157,181,0.3)'; c.setLineDash([5,5]);
      c.beginPath(); c.moveTo(margin.left,y100); c.lineTo(margin.left+iW,y100); c.stroke(); c.setLineDash([]);
      c.fillStyle='#3d6478'; c.font='10px Space Mono,monospace'; c.textAlign='center';
      sp500.forEach((p,i) => {
        if (i % dateStep === 0 || i === sp500.length-1) {
          c.fillText(p.date.toLocaleDateString('en-US',{month:'short',day:'numeric'}), xScale(p.date.getTime()), H-margin.bottom+15);
        }
      });
      const dl = (series: ChartPoint[], color: string, dash: number[]=[]) => {
        if (!series?.length) return;
        c.strokeStyle=color; c.lineWidth=2; c.setLineDash(dash);
        c.beginPath(); series.forEach((p,i) => { const x=xScale(p.date.getTime()),y=yScale(p.value); i===0?c.moveTo(x,y):c.lineTo(x,y); }); c.stroke(); c.setLineDash([]);
      };
      portfolio.tickers.filter(t => this.chartData[t]).forEach((ticker, idx) => { dl(this.chartData[ticker], TICKER_COLORS[idx%TICKER_COLORS.length]+'55', [2,2]); });
      dl(sp500, 'rgba(122,157,181,0.9)', [5,3]);
      dl(portfolioLine, portfolio.color);
    };

    drawStatic(ctx);

    // Legend + stats
    const portRet = portfolioLine.length ? portfolioLine[portfolioLine.length-1].value - 100 : 0;
    const sp500Ret= sp500[sp500.length-1].value - 100;
    const alpha   = portRet - sp500Ret;
    const setStatEl = (id: string, val: number) => {
      const el = this.el(id);
      el.textContent = `${val>=0?'+':''}${val.toFixed(2)}%`;
      el.style.color = val >= 0 ? 'var(--green)' : 'var(--red)';
    };
    setStatEl('perf-stat-portfolio', portRet);
    setStatEl('perf-stat-sp500',     sp500Ret);
    setStatEl('perf-stat-alpha',     alpha);

    // Hover crosshair
    canvas.onmousemove = (e: MouseEvent) => {
      const rect   = canvas.getBoundingClientRect();
      const mx     = e.clientX - rect.left;
      const ts     = minD + (mx - margin.left) / iW * (maxD - minD);
      const sp     = sp500.reduce((a,b) => Math.abs(b.date.getTime()-ts) < Math.abs(a.date.getTime()-ts) ? b : a);
      const pLine  = portfolioLine.reduce((a,b) => Math.abs(b.date.getTime()-ts) < Math.abs(a.date.getTime()-ts) ? b : a);
      const tooltip= this.el('perf-tooltip');
      if (!tooltip) return;
      const pr = pLine.value-100, sr = sp.value-100;
      tooltip.style.display = 'block';
      tooltip.innerHTML = `<div style="color:var(--text3);font-size:10px;margin-bottom:6px;">${sp.date.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
        <div style="color:${portfolio.color};margin-bottom:3px;">${portfolio.label}: <strong>${pr>=0?'+':''}${pr.toFixed(2)}%</strong></div>
        <div style="color:rgba(122,157,181,0.9);">S&P 500: <strong>${sr>=0?'+':''}${sr.toFixed(2)}%</strong></div>
        <div style="color:${pr-sr>=0?'var(--green)':'var(--red)'};font-size:10px;margin-top:4px;">Alpha: ${pr-sr>=0?'+':''}${(pr-sr).toFixed(2)}%</div>`;
      let tx = mx + 15; let ty = e.clientY - rect.top - 10;
      if (tx + 200 > W) tx -= 210;
      tooltip.style.left = tx + 'px'; tooltip.style.top = ty + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // reset (not accumulate) — prevents scale inflation on each mousemove
      ctx.clearRect(0, 0, W, H);
      drawStatic(ctx);
      const cx = xScale(sp.date.getTime());
      ctx.strokeStyle='rgba(0,212,255,0.3)'; ctx.lineWidth=1; ctx.setLineDash([3,3]);
      ctx.beginPath(); ctx.moveTo(cx,margin.top); ctx.lineTo(cx,margin.top+iH); ctx.stroke(); ctx.setLineDash([]);
    };
    canvas.onmouseleave = () => {
      const tooltip = this.el('perf-tooltip');
      if (tooltip) tooltip.style.display = 'none';
    };
  }

  private buildTickerCards(portfolio: Portfolio, dataMap: Record<string, ChartPoint[]>): void {
    const container = this.el('perf-ticker-table');
    if (!container) return;
    const cards = portfolio.tickers.filter(t => dataMap[t]).map(ticker => {
      const series = dataMap[ticker];
      const ret    = series[series.length-1].value - 100;
      const color  = ret >= 0 ? 'var(--green)' : 'var(--red)';
      const arrow  = ret >= 0 ? '▲' : '▼';
      const pts    = series.map((p,i) => `${i/(series.length-1)*100},${30-(p.value-series[0].value+30)/60*30}`).join(' ');
      return `<div style="background:var(--panel);border:1px solid var(--border);border-radius:3px;padding:10px 12px;cursor:pointer;" onclick="(window.loadChartTicker||function(){})(${JSON.stringify(ticker)})">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span style="font-family:var(--mono);font-size:12px;font-weight:700;color:var(--accent);">${esc(ticker)}</span>
          <span style="color:${color};font-size:12px;font-weight:700;">${arrow} ${ret>=0?'+':''}${ret.toFixed(2)}%</span>
        </div>
        <div style="height:30px;margin-top:6px;">
          <svg viewBox="0 0 100 30" style="width:100%;height:100%;" preserveAspectRatio="none">
            <polyline points="${pts}" fill="none" stroke="${ret>=0?'#2ed573':'#ff4757'}" stroke-width="1.5"/>
          </svg>
        </div>
      </div>`;
    });
    const sorted = portfolio.tickers.filter(t => dataMap[t]).map(t => ({ t, ret: dataMap[t][dataMap[t].length-1].value-100 })).sort((a,b) => b.ret-a.ret);
    if (sorted.length) {
      this.el('perf-stat-best').textContent  = `${sorted[0].t} +${sorted[0].ret.toFixed(1)}%`;
      this.el('perf-stat-worst').textContent = `${sorted[sorted.length-1].t} ${sorted[sorted.length-1].ret.toFixed(1)}%`;
    }
    container.innerHTML = cards.join('');
  }
}

function esc(s: unknown): string { return String(s??'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
